import { BasicModel } from '../../../../loco-sync/typeUtils';
import {
  WorkoutPublishErrors,
  validateWorkoutForPublish,
  Workout,
} from '@strength/domain-workout';
import { useMemo } from 'react';
import { BlockDraftAsyncDomain } from '@strength/domain';
import { MArgs } from '@strength/loco-sync';
import { DateTime } from 'luxon';
import { useMetrics } from '../../../hooks/useMetrics';

export type AnyPublishTask = BasicModel<'BlockPublishTask'>;

export type WorkoutPublishErrorsById = {
  workoutId: string;
  errors: WorkoutPublishErrors;
};

const STALLED_PUBLISH_TASK_THRESHOLD_SECONDS = 30;

export function isNowPastLastTimePlusThreshold(
  times: (string | null | undefined)[],
  now: DateTime,
): boolean {
  let lastTime: DateTime | null = null;
  for (const timeISO of times) {
    if (timeISO == null) {
      continue;
    }
    const time = DateTime.fromISO(timeISO);
    if (lastTime === null || time > lastTime) {
      lastTime = time;
    }
  }

  if (lastTime === null) {
    return false;
  }

  return (
    now.diff(lastTime, 'seconds').seconds >
    STALLED_PUBLISH_TASK_THRESHOLD_SECONDS
  );
}

export function usePublishErrorsForWorkouts<W extends Workout>(
  workouts: W[],
  publishTask: AnyPublishTask | undefined,
  extraValidation?: (workout: W) => WorkoutPublishErrors | undefined,
): WorkoutPublishErrorsById[] {
  const { getMetrics } = useMetrics();
  return useMemo(() => {
    const errorsById: WorkoutPublishErrorsById[] = [];
    if (!publishTask || publishTask.status !== 'failed') {
      return errorsById;
    }

    Object.keys(publishTask.failedWorkouts).forEach((id) => {
      const workout = workouts.find((w) => w.workoutId === id);
      if (!workout) {
        return;
      }
      const errors = validateWorkoutForPublish(workout, getMetrics(true));

      // filter out errors about missing sections from the workout-level errors,
      // but preserve all other errors (including exercise-level errors)
      let filteredErrors: WorkoutPublishErrors | null = null;
      if (errors) {
        filteredErrors = new Map(errors.entries());
        const errorsForWorkout = filteredErrors.get(workout.workoutId);
        if (errorsForWorkout) {
          const filteredWorkoutErrors = errorsForWorkout.filter(
            (err) => err[0] !== 'workoutMustHaveOneSection',
          );
          if (filteredWorkoutErrors.length > 0) {
            filteredErrors.set(workout.workoutId, filteredWorkoutErrors);
          } else {
            filteredErrors.delete(workout.workoutId);
          }
        }
        // If the map is now empty, set to null
        if (filteredErrors.size === 0) {
          filteredErrors = null;
        }
      }

      const extraErrors = extraValidation?.(workout);
      const mergedErrors = mergeWorkoutPublishErrors(
        filteredErrors,
        extraErrors,
      );
      if (mergedErrors) {
        errorsById.push({
          workoutId: workout.workoutId,
          errors: mergedErrors,
        });
      }
    });

    return errorsById;
  }, [extraValidation, publishTask, workouts, getMetrics]);
}

function mergeWorkoutPublishErrors(
  a: WorkoutPublishErrors | null | undefined,
  b: WorkoutPublishErrors | null | undefined,
): WorkoutPublishErrors | null | undefined {
  if (!a) {
    return b;
  } else if (!b) {
    return a;
  }

  for (const [key, bValue] of b.entries()) {
    const aValue = a.get(key);
    if (!aValue) {
      a.set(key, bValue);
    } else {
      a.set(key, [...aValue, ...bValue]);
    }
  }

  return a;
}

// TODO: Would be better if this was a bulk mutation
// In which case this should probably return edits
export function deletePublishTasks(
  publishTask: AnyPublishTask | undefined,
  mutate: (args: MArgs) => void,
) {
  if (!publishTask) {
    return;
  }

  mutate({
    module: BlockDraftAsyncDomain.Module,
    type: 'Publish',
    input: {
      blockId: publishTask.blockId,
    },
  });
}
